import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { aiChatToolNames, type AiChatToolName } from '../../../server/ai-chat/tool-names.ts';
import type { AskPlatoMessagePart } from './use-ask-plato-chat.ts';

type ToolCallPart = Extract<AskPlatoMessagePart, { type: 'toolCall' }>;

const knownToolNames: readonly string[] = aiChatToolNames;

function isKnownTool(name: string): name is AiChatToolName {
    return knownToolNames.includes(name);
}

function getRunningCaption(t: TFunction<'ask-plato'>, name: AiChatToolName): string {
    switch (name) {
        case 'find_plato_page':
            return t('tool__find_plato_page__running');
        case 'get_customer_details':
            return t('tool__get_customer_details__running');
        case 'get_customer_financials':
            return t('tool__get_customer_financials__running');
        case 'get_customer_notes':
            return t('tool__get_customer_notes__running');
        case 'get_customer_segment_aggregates':
            return t('tool__get_customer_segment_aggregates__running');
        case 'get_daily_order_intake':
            return t('tool__get_daily_order_intake__running');
        case 'get_data_freshness':
            return t('tool__get_data_freshness__running');
        case 'get_monthly_trend':
            return t('tool__get_monthly_trend__running');
        case 'get_note':
            return t('tool__get_note__running');
        case 'get_pinned_customer_notes':
            return t('tool__get_pinned_customer_notes__running');
        case 'get_quotes_orders_aggregates':
            return t('tool__get_quotes_orders_aggregates__running');
        case 'get_revenue_by_year':
            return t('tool__get_revenue_by_year__running');
        case 'get_sales_summary':
            return t('tool__get_sales_summary__running');
        case 'get_sales_totals':
            return t('tool__get_sales_totals__running');
        case 'get_team_members':
            return t('tool__get_team_members__running');
        case 'get_team_overview':
            return t('tool__get_team_overview__running');
        case 'get_team_stats':
            return t('tool__get_team_stats__running');
        case 'get_top_categories':
            return t('tool__get_top_categories__running');
        case 'get_top_clerks':
            return t('tool__get_top_clerks__running');
        case 'get_top_customers':
            return t('tool__get_top_customers__running');
        case 'get_top_industries':
            return t('tool__get_top_industries__running');
        case 'get_top_product_groups':
            return t('tool__get_top_product_groups__running');
        case 'get_top_products':
            return t('tool__get_top_products__running');
        case 'get_top_sales_reps':
            return t('tool__get_top_sales_reps__running');
        case 'get_top_storage_locations':
            return t('tool__get_top_storage_locations__running');
        case 'get_top_suppliers':
            return t('tool__get_top_suppliers__running');
    }
}

function getDoneCaption(t: TFunction<'ask-plato'>, name: AiChatToolName, search: string | undefined): string {
    switch (name) {
        case 'find_plato_page':
            return t('tool__find_plato_page__done');
        case 'get_customer_details':
            return t('tool__get_customer_details__done');
        case 'get_customer_financials':
            return t('tool__get_customer_financials__done');
        case 'get_customer_notes':
            return search === undefined
                ? t('tool__get_customer_notes__done')
                : t('tool__get_customer_notes__done_search', { search });
        case 'get_customer_segment_aggregates':
            return t('tool__get_customer_segment_aggregates__done');
        case 'get_daily_order_intake':
            return t('tool__get_daily_order_intake__done');
        case 'get_data_freshness':
            return t('tool__get_data_freshness__done');
        case 'get_monthly_trend':
            return t('tool__get_monthly_trend__done');
        case 'get_note':
            return t('tool__get_note__done');
        case 'get_pinned_customer_notes':
            return t('tool__get_pinned_customer_notes__done');
        case 'get_quotes_orders_aggregates':
            return t('tool__get_quotes_orders_aggregates__done');
        case 'get_revenue_by_year':
            return t('tool__get_revenue_by_year__done');
        case 'get_sales_summary':
            return t('tool__get_sales_summary__done');
        case 'get_sales_totals':
            return t('tool__get_sales_totals__done');
        case 'get_team_members':
            return t('tool__get_team_members__done');
        case 'get_team_overview':
            return search === undefined
                ? t('tool__get_team_overview__done')
                : t('tool__get_team_overview__done_search', { search });
        case 'get_team_stats':
            return search === undefined
                ? t('tool__get_team_stats__done')
                : t('tool__get_team_stats__done_search', { search });
        case 'get_top_categories':
            return search === undefined
                ? t('tool__get_top_categories__done')
                : t('tool__get_top_categories__done_search', { search });
        case 'get_top_clerks':
            return search === undefined
                ? t('tool__get_top_clerks__done')
                : t('tool__get_top_clerks__done_search', { search });
        case 'get_top_customers':
            return search === undefined
                ? t('tool__get_top_customers__done')
                : t('tool__get_top_customers__done_search', { search });
        case 'get_top_industries':
            return search === undefined
                ? t('tool__get_top_industries__done')
                : t('tool__get_top_industries__done_search', { search });
        case 'get_top_product_groups':
            return search === undefined
                ? t('tool__get_top_product_groups__done')
                : t('tool__get_top_product_groups__done_search', { search });
        case 'get_top_products':
            return search === undefined
                ? t('tool__get_top_products__done')
                : t('tool__get_top_products__done_search', { search });
        case 'get_top_sales_reps':
            return search === undefined
                ? t('tool__get_top_sales_reps__done')
                : t('tool__get_top_sales_reps__done_search', { search });
        case 'get_top_storage_locations':
            return search === undefined
                ? t('tool__get_top_storage_locations__done')
                : t('tool__get_top_storage_locations__done_search', { search });
        case 'get_top_suppliers':
            return search === undefined
                ? t('tool__get_top_suppliers__done')
                : t('tool__get_top_suppliers__done_search', { search });
    }
}

export function useToolCaption(): (part: ToolCallPart) => string {
    const { t } = useTranslation('ask-plato');

    return (part) => {
        switch (part.status) {
            case 'running':
                return isKnownTool(part.name) ? getRunningCaption(t, part.name) : t('tool__generic__running');
            case 'failed':
                return t('tool__failed');
            case 'interrupted':
                return t('tool__interrupted');
            case 'done':
                return isKnownTool(part.name)
                    ? getDoneCaption(t, part.name, part.search)
                    : t('tool__generic__done');
        }
    };
}
